/**
 * Verification Export Dialog
 *
 * Dialog for exporting verification session as Excel.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { VerificationSession } from '../../types/verification';
import { downloadExportedExcel } from '../../services/verificationService';

interface VerificationExportDialogProps {
  open: boolean;
  session: VerificationSession | null;
  onClose: () => void;
}

const VerificationExportDialog: React.FC<VerificationExportDialogProps> = ({
  open,
  session,
  onClose,
}) => {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleExport = async () => {
    if (!session) return;

    setIsExporting(true);
    setError(null);

    try {
      const filename = `設計検証項目_${session.title}_${session.id.slice(0, 8)}.xlsx`;
      await downloadExportedExcel(session.id, filename);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('verification.export.error'));
    } finally {
      setIsExporting(false);
    }
  };

  if (!session) return null;

  const totalItems = session.units.reduce((sum, u) => sum + u.items.length, 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('verification.export.dialogTitle')}</DialogTitle>
      <DialogContent>
        <Box sx={{ py: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('verification.export.sessionLabel')}: {session.title}
          </Typography>
          {session.modelName && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('verification.modelName')}: {session.modelName}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            {session.units.length} {t('verification.units')} / {totalItems} {t('verification.items')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isExporting}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleExport}
          disabled={isExporting}
          startIcon={isExporting ? <CircularProgress size={16} /> : <DownloadIcon />}
        >
          {isExporting ? t('verification.export.exporting') : t('verification.export.download')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VerificationExportDialog;
